import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { guestPortalApi } from '../../lib/api';
import { useGuestAuth } from '../../lib/guest-auth-context';
import {
  Hotel,
  ArrowLeft,
  Loader2,
  Bell,
  Sparkles,
  Wrench,
  UtensilsCrossed,
  Truck,
  Clock,
  HelpCircle,
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { toast } from 'sonner';

const categories = [
  { value: 'HOUSEKEEPING', label: 'Housekeeping', icon: Sparkles, description: 'Room cleaning, towels, amenities' },
  { value: 'MAINTENANCE', label: 'Maintenance', icon: Wrench, description: 'Repairs, AC, plumbing issues' },
  { value: 'ROOM_SERVICE', label: 'Room Service', icon: UtensilsCrossed, description: 'Food & beverage to your room' },
  { value: 'CONCIERGE', label: 'Concierge', icon: HelpCircle, description: 'Tours, reservations, assistance' },
  { value: 'TRANSPORT', label: 'Transport', icon: Truck, description: 'Airport transfer, taxi booking' },
  { value: 'WAKE_UP', label: 'Wake-up Call', icon: Clock, description: 'Morning wake-up call service' },
  { value: 'OTHER', label: 'Other', icon: Bell, description: 'Any other request' },
];

export default function NewServiceRequestPage() {
  const navigate = useNavigate();
  const { token, isAuthenticated, isLoading: authLoading } = useGuestAuth();
  const [loading, setLoading] = useState(false);
  const [properties, setProperties] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [form, setForm] = useState({
    property_id: '',
    reservation_id: '',
    category: '',
    priority: 'NORMAL',
    title: '',
    description: '',
  });

  useEffect(() => {
    // Wait for auth to finish loading before checking authentication
    if (authLoading) return;
    
    if (!isAuthenticated) {
      navigate('/portal/login');
      return;
    }
    
    const fetchData = async () => {
      try {
        const [propsRes, reservationsRes] = await Promise.all([
          guestPortalApi.listProperties(),
          guestPortalApi.listReservations(token),
        ]);
        setProperties(propsRes.data || []);
        setReservations(reservationsRes.data || []);
        
        // Auto-select if only one active reservation
        const activeRes = (reservationsRes.data || []).filter(r => r.status === 'CHECKED_IN');
        if (activeRes.length === 1) {
          setForm(f => ({
            ...f,
            property_id: activeRes[0].property_id,
            reservation_id: activeRes[0].id,
          }));
        }
      } catch (error) {
        console.error('Failed to load data:', error);
      }
    };
    
    fetchData();
  }, [authLoading, isAuthenticated, token, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.property_id || !form.category || !form.title) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      setLoading(true);
      await guestPortalApi.createServiceRequest(token, {
        property_id: form.property_id,
        reservation_id: form.reservation_id || null,
        category: form.category,
        priority: form.priority,
        title: form.title,
        description: form.description || null,
      });
      
      toast.success('Service request submitted');
      navigate('/portal/dashboard');
    } catch (error) {
      console.error('Failed to submit request:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit request');
    } finally {
      setLoading(false);
    }
  };

  const getCategoryIcon = (value) => {
    const cat = categories.find(c => c.value === value);
    return cat ? cat.icon : Bell;
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-muted/30" data-testid="new-service-request-page">
      {/* Header */}
      <header className="border-b bg-background/95 backdrop-blur sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/portal" className="flex items-center gap-2">
            <Hotel className="h-8 w-8 text-primary" />
            <span className="text-xl font-bold">HMS Hotels</span>
          </Link>
          <Button variant="ghost" onClick={() => navigate('/portal/dashboard')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8">
        <div className="max-w-2xl mx-auto">
          <h1 className="text-2xl font-bold mb-2">New Service Request</h1>
          <p className="text-muted-foreground mb-8">
            Tell us what you need and we will take care of it
          </p>

          <form onSubmit={handleSubmit}>
            {/* Category Selection */}
            <Card className="mb-6">
              <CardHeader>
                <CardTitle className="text-lg">What do you need help with?</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {categories.map(cat => {
                    const Icon = cat.icon;
                    return (
                      <button
                        key={cat.value}
                        type="button"
                        onClick={() => setForm({ ...form, category: cat.value })}
                        className={`p-4 rounded-lg border text-left transition-all ${
                          form.category === cat.value 
                            ? 'border-primary bg-primary/5 ring-2 ring-primary' 
                            : 'hover:border-primary/50'
                        }`}
                        data-testid={`category-${cat.value}`}
                      >
                        <Icon className={`h-6 w-6 mb-2 ${form.category === cat.value ? 'text-primary' : 'text-muted-foreground'}`} />
                        <p className="font-medium text-sm">{cat.label}</p>
                        <p className="text-xs text-muted-foreground">{cat.description}</p>
                      </button>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            {/* Request Details */}
            <Card className="mb-6">
              <CardHeader>
                <CardTitle className="text-lg">Request Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Property *</Label>
                    <Select 
                      value={form.property_id} 
                      onValueChange={(v) => setForm({ ...form, property_id: v })}
                    >
                      <SelectTrigger data-testid="property-select">
                        <SelectValue placeholder="Select property" />
                      </SelectTrigger>
                      <SelectContent>
                        {properties.map(prop => (
                          <SelectItem key={prop.id} value={prop.id}>
                            {prop.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Reservation (Optional)</Label>
                    <Select 
                      value={form.reservation_id} 
                      onValueChange={(v) => setForm({ ...form, reservation_id: v === 'none' ? '' : v })}
                    >
                      <SelectTrigger data-testid="reservation-select">
                        <SelectValue placeholder="Select reservation" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No reservation</SelectItem>
                        {reservations.filter(r => r.property_id === form.property_id || !form.property_id).map(res => (
                          <SelectItem key={res.id} value={res.id}>
                            {res.confirmation_number} - {res.properties?.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Priority</Label>
                  <Select 
                    value={form.priority} 
                    onValueChange={(v) => setForm({ ...form, priority: v })}
                  >
                    <SelectTrigger data-testid="priority-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="LOW">Low - When convenient</SelectItem>
                      <SelectItem value="NORMAL">Normal - Today</SelectItem>
                      <SelectItem value="HIGH">High - Within the hour</SelectItem>
                      <SelectItem value="URGENT">Urgent - Immediately</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Title *</Label>
                  <Input
                    placeholder="Brief description of your request"
                    value={form.title}
                    onChange={(e) => setForm({ ...form, title: e.target.value })}
                    data-testid="title-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Additional Details</Label>
                  <Textarea
                    placeholder="Provide any additional information that might help us assist you better..."
                    value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    rows={4}
                    data-testid="description-input"
                  />
                </div>
              </CardContent>
              <CardFooter className="flex gap-4">
                <Button 
                  type="button" 
                  variant="outline" 
                  className="flex-1"
                  onClick={() => navigate('/portal/dashboard')}
                >
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  className="flex-1"
                  disabled={loading || !form.category || !form.property_id || !form.title}
                  data-testid="submit-request-btn"
                >
                  {loading ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Bell className="h-4 w-4 mr-2" />
                  )}
                  Submit Request
                </Button>
              </CardFooter>
            </Card>
          </form>
        </div>
      </main>
    </div>
  );
}
